import { Component, ErrorInfo, ReactNode } from 'react';
import { Button, Layout, Result } from 'antd';
import * as Sentry from '@sentry/react';
import i18next from 'i18next';

import AppUpdatedPage from '@/components/pages/AppUpdatedPage';
import { isChunkLoadError } from '@/utils/chunkLoadError';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * The router's own ErrorBoundary (wired via react-router's errorElement) only
 * catches errors thrown from within the routed tree. It can't help here: `App`
 * itself is lazy-loaded in main.tsx, so a stale-chunk failure on that import
 * throws before the router — and its errorElement — ever mount. This class
 * component is the outermost boundary, wrapping that lazy `<App />`, so the
 * same chunk-load failure that produced the reported blank page is caught.
 */
class RootErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isChunkLoadError(error)) {
      return;
    }

    Sentry.captureException(error, {
      tags: { errorBoundary: 'Root' },
      extra: { componentStack: errorInfo.componentStack },
    });
  }

  render() {
    const { error } = this.state;

    if (error && isChunkLoadError(error)) {
      return <AppUpdatedPage />;
    }

    if (error) {
      return (
        <Layout style={{ minHeight: '100vh' }}>
          <div className="min-h-screen w-full flex items-center justify-center">
            <Result
              status="500"
              title={i18next.t('errors.boundary.title')}
              subTitle={i18next.t('errors.boundary.message')}
              extra={
                <Button type="primary" onClick={() => window.location.reload()}>
                  {i18next.t('errors.boundary.reload')}
                </Button>
              }
            />
          </div>
        </Layout>
      );
    }

    return this.props.children;
  }
}

export default RootErrorBoundary;
